import { useQuery } from '@tanstack/react-query';
import { ordersApi } from '../../api/orders';
import { queryKeys } from '../queryClient';

/** Admin dashboard ecommerce revenue/order overview — see OrdersController.AnalyticsSummary
 * and its doc comment on why this reads OrderService's own data, not BillingService's
 * booking-oriented AccountingSummary. Manager+ only (gateway-enforced). Polls every 30s so the
 * dashboard's KPIs/charts stay current without a manual refresh — cheap aggregate query, and
 * OrderHub's live order-status events (see useOrderTracking) don't currently invalidate this
 * key themselves, so polling is the simplest way to keep it fresh. */
export function useOrderAnalyticsSummary(days: number) {
  return useQuery({
    queryKey: queryKeys.orderAnalyticsSummary(days),
    queryFn: () => ordersApi.analyticsSummary(days),
    refetchInterval: 30_000,
  });
}

/** Real units-sold aggregation — same data source the storefront's "Hot Sellers" widget uses,
 * reused here for an admin-facing top-products table. */
export function useOrderTopProducts(limit: number, days: number) {
  return useQuery({
    queryKey: queryKeys.orderTopProducts(limit, days),
    queryFn: () => ordersApi.topProducts(limit, days),
    refetchInterval: 30_000,
  });
}

/** Order-lifecycle funnel (Placed → Processing → Completed / Cancelled) — see
 * OrdersController.Funnel and OrderFunnelHandler's doc comment for why this is built from the
 * real OrderHistory audit trail rather than page-view/add-to-cart events this system doesn't
 * track. */
export function useOrderFunnel(days: number) {
  return useQuery({
    queryKey: queryKeys.orderAnalyticsFunnel(days),
    queryFn: () => ordersApi.analyticsFunnel(days),
  });
}

/** Dashboard KPI row: today/this-week orders+revenue, pending backlog, active/total customers
 * — see OrdersController.AnalyticsOverview. Polls every 30s, same reasoning as
 * useOrderAnalyticsSummary. */
export function useOrderAnalyticsOverview() {
  return useQuery({
    queryKey: queryKeys.orderAnalyticsOverview(),
    queryFn: () => ordersApi.analyticsOverview(),
    refetchInterval: 30_000,
  });
}

/** Cross-order audit log (every order's status changes) — see OrdersController.AllHistory. */
export function useAllOrderHistory(filters: { status?: string; cursor?: string; pageSize?: number } = {}) {
  return useQuery({
    queryKey: ['order-history-all', filters.status ?? 'all', filters.cursor ?? 'first'],
    queryFn: () => ordersApi.allHistory(filters),
  });
}

/** Monthly retention by first-order cohort — see OrdersController.Cohorts and
 * CohortAnalysisHandler's doc comment. */
export function useOrderCohorts(months: number) {
  return useQuery({
    queryKey: queryKeys.orderAnalyticsCohorts(months),
    queryFn: () => ordersApi.analyticsCohorts(months),
  });
}
